import React, { useRef, useState, useEffect } from 'react';
import { usePostHog } from 'posthog-js/react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import DatePicker from 'react-datepicker';
import SearchIcon from '@mui/icons-material/Search';
import AmountWithCurrency from '@/components/AmountWithCurrency';
import { useGetRefunds } from '@/api/hooks/refunds/useGetRefunds';
import moment from 'moment';
import loader from '@/assets/img/loader.svg';
import {
  ENTITY_TYPE,
  REFUND_STATUSES,
  REFUND_SUB_STATUS,
  REFUND_TYPES,
  REFUND_PATH,
} from '@/utility';
import EditManualRefundModal from '@/views/Facilities/CreateOrEditManualRefund/EditManualRefundModal';
import { EditRounded, HistoryRounded } from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '@/config';
import { RefundsApi } from '../../api/clients/refunds/refunds.api';
import PayeeCategorySelect from '@/components/PayeeCategorySelect';
import { BusinessUnitsApi } from '@/api/clients/business-units/business-units.api';
import FacilitySelect from '@/components/FacilitySelect';
import { StatusHistoryModal } from '@/views/Clients/FacilityTransaction/StatusHistory/StatusHistoryModal';
import { toast } from 'react-toastify';
import CustomPaymentTypeSelect from '@/components/CustomPaymentTypeSelect';
import ExceptionsLongMenu from '@/components/ExceptionsLongMenu/ExceptionsLongMenu';

const PayeeCommunicationExceptions = ({ role = 'facility' }) => {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const isCustomPaymentTypesEnabled = posthog?.isFeatureEnabled(
    'new-custom-payment-types-workflow',
  );
  const listInnerRef = useRef();
  const [currentInfo, setCurrentInfo] = useState(null);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [allowedRefundTypes, setAllowedRefundTypes] = useState([]);
  const [filteredBusinessUnits, setFilteredBusinessUnits] = useState([]);
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const {
    payments,
    loading,
    startDate,
    setPartialState,
    endDate,
    payeeCategory,
    search,
    hasMore,
    offSet,
    sortBy,
    handleSearch,
    selectedBusinessUnit,
    refetchRefunds,
    facility,
    customPaymentTypeId,
  } = useGetRefunds({
    refundLocation: REFUND_PATH.COMMUNICATION_EXCEPTIONS,
    defaultValues: {
      status: REFUND_STATUSES.EXCEPTION,
      subStatus: REFUND_SUB_STATUS.COMMUNICATION,
    },
  });

  useEffect(() => {
    const fetchBusinessUnits = async () => {
      try {
        const response = await BusinessUnitsApi.getBusinessUnitsForForm();
        setBusinessUnits(response);
      } catch (error) {
        console.error('Error fetching business units:', error);
      }
    };

    const fetchAllowedRefundTypes = async () => {
      try {
        const response = await RefundsApi.getAllowedRefundTypes();
        if (role !== 'admin') {
          setAllowedRefundTypes(response.data.allowed_refund_types);
        }
      } catch (error) {
        console.error('Error fetching allowed refund types:', error);
      }
    };

    fetchBusinessUnits();

    fetchAllowedRefundTypes();
  }, []);

  const handleSearchChange = (e) => {
    setPartialState({ search: e.target.value });
    handleSearch(e.target.value);
  };

  const handleScroll = () => {
    if (listInnerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listInnerRef.current;
      if (
        scrollTop + clientHeight >= scrollHeight - 10 &&
        hasMore &&
        !loading
      ) {
        setPartialState({ offSet: offSet + 25 });
      }
    }
  };

  const sortByColumn = (columnName) => {
    setPartialState({
      sortBy: {
        name: columnName,
        order: sortBy.name === columnName ? (sortBy.order === 1 ? -1 : 1) : 1,
      },
    });
  };

  const sortArrow = (columnName) => {
    return columnName === sortBy.name ? (sortBy.order === 1 ? '↓' : '↑') : '';
  };

  const fetchStatusHistory = async () => {
    if (!selectedRefund) {
      setStatusHistory([]);
      return;
    }

    try {
      const fetchedStatusHistory = (
        await axios.get(
          API_BASE_URL +
            `/status_history/communication-exceptions/` +
            selectedRefund,
        )
      ).data;

      if (fetchedStatusHistory.ach_data_backfill_error) {
        toast.error(fetchedStatusHistory.ach_data_backfill_error);
      }

      setStatusHistory(fetchedStatusHistory.history);
    } catch (error) {
      console.error('Error fetching status history:', error);
      toast.error('Failed to fetch status history');
    }
  };

  useEffect(() => {
    fetchStatusHistory();
  }, [selectedRefund]);

  const handleRetryPullingAch = async () => {
    fetchStatusHistory();
  };

  const onFacilitySelect = (facilityId) => {
    setPartialState({ facility: facilityId });
    setFilteredBusinessUnits(
      businessUnits.filter((unit) => unit.facility_id == facilityId),
    );
  };

  return (
    <div style={{ height: '75vh', overflow: 'hidden' }}>
      <div>
        <div
          style={{
            display: 'flex',
            gap: '10px',
            flexWrap: 'wrap',
            margin: '20px 0',
          }}>
          <FacilitySelect
            value={facility || ''}
            onChange={onFacilitySelect}
            placeholderKey='exceptions.filters.facilities'
            parentKey='exceptions'
            childKey='processing_exceptions'
            permission='read'
          />
          <div>
            <div className='custom-search-box'>
              <select
                id='business-unit'
                className='full-width-elem'
                style={{ height: '35px' }}
                value={selectedBusinessUnit}
                onChange={(e) =>
                  setPartialState({ selectedBusinessUnit: e.target.value })
                }>
                <option value=''>
                  {t('exceptions.filters.allBusinessUnits')}
                </option>
                {(filteredBusinessUnits?.length
                  ? filteredBusinessUnits
                  : businessUnits
                ).map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name}
                  </option>
                ))}
              </select>
              <span className='search-icon'></span>
            </div>
          </div>
          {(role === 'admin' || !isCustomPaymentTypesEnabled) &&
            (allowedRefundTypes.length > 1 || role === 'admin') && (
              <PayeeCategorySelect
                role={role}
                allowedRefundTypes={allowedRefundTypes}
                payeeCategory={payeeCategory}
                onChange={(e) =>
                  setPartialState({ payeeCategory: e.target.value })
                }
              />
            )}
          {(role === 'admin' || isCustomPaymentTypesEnabled) && (
            <CustomPaymentTypeSelect
              value={customPaymentTypeId}
              onChange={(e) =>
                setPartialState({ customPaymentTypeId: e.target.value })
              }
            />
          )}
          <div>
            {t('exceptions.dateFilters.from')}&nbsp;&nbsp;
            <DatePicker
              className='full-width-elem-date-picker'
              selected={startDate}
              onChange={(date) => setPartialState({ startDate: date })}
            />
          </div>
          <div>
            {t('exceptions.dateFilters.to')}&nbsp;&nbsp;
            <DatePicker
              className='full-width-elem-date-picker'
              selected={endDate}
              onChange={(date) => setPartialState({ endDate: date })}
            />
          </div>
          <div>
            <div className='custom-search-box'>
              <input
                type='search'
                className='search-field'
                style={{ height: '35px' }}
                value={search}
                onChange={handleSearchChange}
                placeholder={t('exceptions.search.placeholder')}
              />
              <span className='search-icon'>
                <SearchIcon />
              </span>
            </div>
          </div>
        </div>
        {loading && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
            }}>
            <img src={loader} alt={t('exceptions.loading')} />
          </div>
        )}
        <div
          style={{
            overflow: 'auto',
            height: '70vh',
            width: '100%',
            position: 'relative',
          }}
          ref={listInnerRef}
          onScroll={handleScroll}>
          <Table width='100%' id='template'>
            <TableHead>
              <TableRow style={{ cursor: 'pointer', userSelect: 'none' }}>
                <TableCell onClick={() => sortByColumn('created_at')}>
                  {t('exceptions.table.dateCreated')}
                  {sortArrow('created_at')}
                </TableCell>
                <TableCell onClick={() => sortByColumn('updated_at')}>
                  {t('exceptions.table.lastUpdated')} {sortArrow('updated_at')}
                </TableCell>
                <TableCell onClick={() => sortByColumn('last_name')}>
                  {t('exceptions.table.customerInformation')}{' '}
                  {sortArrow('last_name')}
                </TableCell>
                <TableCell onClick={() => sortByColumn('refund_type')}>
                  {t('common.table.paymentCategory')} {sortArrow('refund_type')}
                </TableCell>
                <TableCell onClick={() => sortByColumn('business_unit_name')}>
                  {t('exceptions.table.businessUnit')}{' '}
                  {sortArrow('business_unit_name')}
                </TableCell>
                <TableCell onClick={() => sortByColumn('facility_name')}>
                  {t('common.facility')} {sortArrow('facility_name')}
                </TableCell>
                <TableCell onClick={() => sortByColumn('refund_amount')}>
                  {t('exceptions.table.amount')} {sortArrow('refund_amount')}
                </TableCell>
                <TableCell onClick={() => sortByColumn('payee_account_number')}>
                  {t('exceptions.table.customerIds')}
                  {sortArrow('payee_account_number')}
                </TableCell>
                <TableCell>
                  {t('exceptions.table.reasonForException')}
                </TableCell>
                <TableCell>{t('exceptions.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            {payments && payments?.length > 0 ? (
              <TableBody>
                {payments.map((refund) => (
                  <TableRow key={refund.id}>
                    <TableCell>
                      {moment(refund.created_at).format('MM/DD/YYYY')}
                      <span style={{ fontSize: '12px' }}>
                        <br />
                        {refund.created_by ||
                          t('exceptions.table.notAvailable')}
                      </span>
                    </TableCell>
                    <TableCell>
                      {moment(refund.updated_at).format('MM/DD/YYYY')}
                      <span style={{ fontSize: '12px' }}>
                        <br />
                        {refund.updated_by ||
                          t('exceptions.table.notAvailable')}
                      </span>
                    </TableCell>
                    <TableCell>
                      {isCustomPaymentTypesEnabled ? (
                        refund.entity_type === ENTITY_TYPE.INDIVIDUAL ? (
                          <>
                            {refund.first_name} {refund.last_name}{' '}
                          </>
                        ) : (
                          <>{refund.business_payee_name} </>
                        )
                      ) : (
                        <>
                          {refund.first_name} {refund.last_name}{' '}
                        </>
                      )}
                      <a
                        title={t('exceptions.actions.editCustomerInfo')}
                        onClick={() => {
                          setCurrentInfo(refund);
                        }}>
                        <EditRounded
                          style={{ cursor: 'pointer', fontSize: '18px' }}
                          fontSize='inherit'
                        />
                      </a>
                      {isCustomPaymentTypesEnabled ? (
                        refund.entity_type === ENTITY_TYPE.INDIVIDUAL &&
                        refund.email ? (
                          <span style={{ display: 'flex' }}>
                            <p style={{ fontSize: '12px' }}>
                              ({refund.email})
                              {refund.has_files_attached ? (
                                <span style={{ fontWeight: 700 }}>
                                  {t('exceptions.table.pdfsAttached')}
                                </span>
                              ) : null}
                            </p>
                          </span>
                        ) : refund.entity_type === ENTITY_TYPE.BUSINESS &&
                          refund.business_payee_name ? (
                          <span style={{ display: 'flex' }}>
                            <p style={{ fontSize: '12px' }}>
                              ({refund.business_payee_name})
                              {refund.has_files_attached ? (
                                <span style={{ fontWeight: 700 }}>
                                  {t('exceptions.table.pdfsAttached')}
                                </span>
                              ) : null}
                            </p>
                          </span>
                        ) : null
                      ) : !!refund.email ||
                        ((refund.refund_type === REFUND_TYPES.INSURANCE ||
                          refund.refund_type === REFUND_TYPES.BUSINESS) &&
                          refund.business_payee_name) ? (
                        <span style={{ display: 'flex' }}>
                          <p style={{ fontSize: '12px' }}>
                            (
                            {refund.refund_type === REFUND_TYPES.INSURANCE ||
                            refund.refund_type === REFUND_TYPES.BUSINESS
                              ? refund.business_payee_name
                              : refund.email}
                            )
                            {refund.has_files_attached ? (
                              <span style={{ fontWeight: 700 }}>
                                {t('exceptions.table.pdfsAttached')}
                              </span>
                            ) : null}
                          </p>
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {isCustomPaymentTypesEnabled
                        ? refund.custom_payment_type ||
                          t('exceptions.table.notApplicable')
                        : refund.refund_type_string}
                    </TableCell>
                    <TableCell>
                      {refund.business_unit_name ||
                        t('exceptions.table.notApplicable')}
                    </TableCell>
                    <TableCell>
                      {refund.facility_name ||
                        t('exceptions.table.notApplicable')}
                    </TableCell>
                    <TableCell>
                      <AmountWithCurrency
                        amount={refund.refund_amount}
                        currency={refund.currency}
                      />
                    </TableCell>
                    <TableCell>
                      {refund.payee_account_number ||
                        t('exceptions.table.notApplicable')}{' '}
                      <br />
                      {refund.client_transaction_id ||
                        t('exceptions.table.notApplicable')}{' '}
                      <br />
                      {refund.id || t('exceptions.table.notApplicable')}
                    </TableCell>
                    <TableCell>
                      {refund.refund_sub_status ||
                        t('checkPayments.table.notAvailable')}
                      <br />
                      {refund.refund_additional_info && (
                        <span style={{ fontSize: '12px' }}>
                          {refund.refund_additional_info}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div
                        style={{
                          display: 'flex',
                          gap: '10px',
                          alignItems: 'center',
                        }}>
                        <HistoryRounded
                          style={{ color: '#3AB795', cursor: 'pointer' }}
                          title='Status History'
                          onClick={() => setSelectedRefund(refund.id)}
                        />
                        <ExceptionsLongMenu
                          refundPath={REFUND_PATH.COMMUNICATION_EXCEPTIONS}
                          refund={refund}
                          refetchRefunds={refetchRefunds}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            ) : (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={11}>
                    {t('exceptions.table.noExceptionDataFound')}
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </div>
      </div>
      {currentInfo && (
        <EditManualRefundModal
          refundLocation={REFUND_PATH.COMMUNICATION_EXCEPTIONS}
          updateStatus={true}
          refund={currentInfo}
          reFetch={refetchRefunds}
          onClose={() => setCurrentInfo(null)}
          role={role}
          editableFields={[
            'email',
            'refundToAddressLine1',
            'refundToAddressLine2',
            'city',
            'state',
            'refundToZip',
            'refundToCountry',
            'comment',
            'payImmediately',
          ]}
        />
      )}
      <StatusHistoryModal
        role={role}
        open={selectedRefund !== null}
        statusHistory={statusHistory}
        onRetryPullingAch={handleRetryPullingAch}
        onClose={() => setSelectedRefund(null)}
        refund={payments.find((r) => r.id === selectedRefund)}
      />
    </div>
  );
};

export default PayeeCommunicationExceptions;
